import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Users, HandHeart, Building2, Check } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { PageSEO } from '../components/PageSEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const JoinPage = () => {
  const [activeTab, setActiveTab] = useState('membership');
  const [loading, setLoading] = useState(false);
  
  // Membership form state
  const [memberForm, setMemberForm] = useState({
    name: '', email: '', phone: '', address: '', occupation: '', interests: [], message: ''
  });
  
  // Volunteer form state
  const [volunteerForm, setVolunteerForm] = useState({
    name: '', email: '', phone: '', skills: [], availability: '', message: ''
  });
  
  // Sponsor form state
  const [sponsorForm, setSponsorForm] = useState({
    company_name: '', contact_name: '', email: '', phone: '', sponsorship_type: '', message: ''
  });

  const interestOptions = ['Arıcılık', 'Eğitim', 'Pazarlama', 'Proje Geliştirme', 'İletişim'];
  const skillOptions = ['Arıcılık Tecrübesi', 'Eğitim/Mentorluk', 'Grafik Tasarım', 'Sosyal Medya', 'Etkinlik Organizasyonu', 'Çeviri'];
  const sponsorshipTypes = ['Proje Sponsorluğu', 'Etkinlik Sponsorluğu', 'Genel Destek', 'Ekipman Bağışı'];

  const handleMembershipSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/membership`, memberForm);
      toast.success('Üyelik başvurunuz alındı!');
      setMemberForm({ name: '', email: '', phone: '', address: '', occupation: '', interests: [], message: '' });
    } catch (error) {
      toast.error('Bir hata oluştu. Lütfen tekrar deneyin.');
    }
    setLoading(false);
  };

  const handleVolunteerSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/volunteer`, volunteerForm);
      toast.success('Gönüllü başvurunuz alındı!');
      setVolunteerForm({ name: '', email: '', phone: '', skills: [], availability: '', message: '' });
    } catch (error) {
      toast.error('Bir hata oluştu. Lütfen tekrar deneyin.');
    }
    setLoading(false);
  };

  const handleSponsorSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/sponsor`, sponsorForm);
      toast.success('Sponsorluk talebiniz alındı!');
      setSponsorForm({ company_name: '', contact_name: '', email: '', phone: '', sponsorship_type: '', message: '' });
    } catch (error) {
      toast.error('Bir hata oluştu. Lütfen tekrar deneyin.');
    }
    setLoading(false);
  };

  const tabs = [
    { id: 'membership', label: 'Üyelik', icon: Users },
    { id: 'volunteer', label: 'Gönüllülük', icon: HandHeart },
    { id: 'sponsor', label: 'Sponsorluk', icon: Building2 }
  ];

  const memberBenefits = [
    'Ücretsiz eğitim programlarına katılım',
    'Ekipman ve malzeme indirimler',
    'Ağ oluşturma etkinlikleri',
    'Market satış ayrıcalıkları',
    'Mentorluk desteği'
  ];

  return (
    <div data-testid="join-page">
      <PageSEO title="Bize Katılın" description="KOSAD ailesine katılın! Üyelik, gönüllülük ve sponsorluk başvuruları. Sürdürülebilir arıcılık için birlikte çalışalım." path="/katil" />
      {/* Hero */}
      <section className="relative py-32 bg-[#1A3625]">
        <div className="container-custom">
          <motion.div 
            className="max-w-3xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="label-uppercase text-[#D4AF37] mb-4 block">Katıl</span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl text-white font-semibold mb-6">
              Ailemize Katılın
            </h1>
            <p className="text-base text-white/80">
              Üye olun, gönüllü olun veya projelerimizi destekleyin. Birlikte daha güçlüyüz.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Tabs */}
      <section className="py-8 bg-white sticky top-20 z-40 border-b border-[#E5DAC8]">
        <div className="container-custom">
          <div className="flex flex-wrap gap-4">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-all ${
                  activeTab === tab.id 
                    ? 'bg-[#1A3625] text-white' 
                    : 'bg-[#F9F8F6] text-[#5C5C5C] hover:bg-[#E5DAC8]'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="w-5 h-5" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="section-padding">
        <div className="container-custom">
          {/* Membership */}
          {activeTab === 'membership' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="grid lg:grid-cols-2 gap-16"
            >
              <div>
                <h2 className="text-3xl text-[#1A3625] mb-6">KOSAD Üyesi Olun</h2>
                <p className="text-[#5C5C5C] mb-8">
                  KOSAD üyesi olarak sürdürülebilir arıcılık hareketinin bir parçası olun. 
                  Üyelik hem bireyler hem de kurumsal başvurular için açıktır.
                </p>
                
                <h3 className="text-lg text-[#1A3625] mb-4">Üyelik Avantajları</h3>
                <ul className="space-y-3 mb-8">
                  {memberBenefits.map((benefit, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <div className="w-6 h-6 bg-[#D4AF37]/20 rounded-full flex items-center justify-center">
                        <Check className="w-4 h-4 text-[#D4AF37]" />
                      </div>
                      <span className="text-[#5C5C5C]">{benefit}</span>
                    </li>
                  ))}
                </ul>
                
                <div className="bg-[#F9F8F6] rounded-2xl p-6">
                  <h4 className="font-medium text-[#1A3625] mb-2">Yıllık Üyelik Aidatı</h4>
                  <p className="text-3xl font-semibold text-[#D4AF37]">₺1.200</p>
                  <p className="text-base text-[#5C5C5C] mt-2">Öğrenciler ve 65 yaş üstü için %50 indirim</p>
                </div>
              </div>
              
              <form onSubmit={handleMembershipSubmit} className="card-premium" data-testid="membership-form">
                <h3 className="text-lg text-[#1A3625] mb-6">Üyelik Başvurusu</h3>
                
                <div className="space-y-4">
                  <input
                    type="text"
                    placeholder="Ad Soyad *"
                    value={memberForm.name}
                    onChange={(e) => setMemberForm({...memberForm, name: e.target.value})}
                    className="input-premium"
                    required
                    data-testid="member-name"
                  />
                  <input
                    type="email"
                    placeholder="E-posta *"
                    value={memberForm.email}
                    onChange={(e) => setMemberForm({...memberForm, email: e.target.value})}
                    className="input-premium"
                    required
                    data-testid="member-email"
                  />
                  <input
                    type="tel"
                    placeholder="Telefon *"
                    value={memberForm.phone}
                    onChange={(e) => setMemberForm({...memberForm, phone: e.target.value})}
                    className="input-premium"
                    required
                    data-testid="member-phone"
                  />
                  <input
                    type="text"
                    placeholder="Adres *"
                    value={memberForm.address}
                    onChange={(e) => setMemberForm({...memberForm, address: e.target.value})}
                    className="input-premium"
                    required
                    data-testid="member-address"
                  />
                  <input
                    type="text"
                    placeholder="Meslek"
                    value={memberForm.occupation}
                    onChange={(e) => setMemberForm({...memberForm, occupation: e.target.value})}
                    className="input-premium"
                    data-testid="member-occupation"
                  />
                  
                  <div>
                    <label className="block text-base font-medium text-[#1A3625] mb-2">İlgi Alanları</label>
                    <div className="flex flex-wrap gap-2">
                      {interestOptions.map((interest) => (
                        <button
                          key={interest}
                          type="button"
                          onClick={() => {
                            const newInterests = memberForm.interests.includes(interest)
                              ? memberForm.interests.filter(i => i !== interest)
                              : [...memberForm.interests, interest];
                            setMemberForm({...memberForm, interests: newInterests});
                          }}
                          className={`px-4 py-2 rounded-full text-base transition-all ${
                            memberForm.interests.includes(interest)
                              ? 'bg-[#1A3625] text-white'
                              : 'bg-[#F9F8F6] text-[#5C5C5C] hover:bg-[#E5DAC8]'
                          }`}
                        >
                          {interest}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  <textarea
                    placeholder="Eklemek istedikleriniz..."
                    value={memberForm.message}
                    onChange={(e) => setMemberForm({...memberForm, message: e.target.value})}
                    className="input-premium min-h-[100px]"
                    data-testid="member-message"
                  />
                  
                  <button 
                    type="submit" 
                    className="btn-primary w-full"
                    disabled={loading}
                    data-testid="member-submit"
                  >
                    {loading ? 'Gönderiliyor...' : 'Başvuru Yap'}
                  </button>
                </div>
              </form>
            </motion.div>
          )}

          {/* Volunteer */}
          {activeTab === 'volunteer' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="grid lg:grid-cols-2 gap-16"
            >
              <div>
                <h2 className="text-2xl text-[#1A3625] mb-6">Gönüllü Olun</h2>
                <p className="text-[#5C5C5C] mb-8">
                  KOSAD gönüllüsü olarak etkinliklerimizde, eğitimlerimizde ve 
                  projelerimizde aktif rol alın. Her türlü beceri ve deneyim değerlidir.
                </p>
                
                <h3 className="text-lg text-[#1A3625] mb-4">Gönüllülük Alanları</h3>
                <div className="grid grid-cols-2 gap-4 mb-8">
                  {['Etkinlik Organizasyonu', 'Eğitim Desteği', 'Sosyal Medya', 'Çeviri', 'Grafik Tasarım', 'Saha Çalışması'].map((area, index) => (
                    <div key={index} className="bg-[#F9F8F6] rounded-xl p-4 text-center">
                      <span className="text-[#5C5C5C]">{area}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <form onSubmit={handleVolunteerSubmit} className="card-premium" data-testid="volunteer-form">
                <h3 className="text-xl text-[#1A3625] mb-6">Gönüllü Başvurusu</h3>
                
                <div className="space-y-4">
                  <input
                    type="text"
                    placeholder="Ad Soyad *"
                    value={volunteerForm.name}
                    onChange={(e) => setVolunteerForm({...volunteerForm, name: e.target.value})}
                    className="input-premium"
                    required
                    data-testid="volunteer-name"
                  />
                  <input
                    type="email"
                    placeholder="E-posta *"
                    value={volunteerForm.email}
                    onChange={(e) => setVolunteerForm({...volunteerForm, email: e.target.value})}
                    className="input-premium"
                    required
                    data-testid="volunteer-email"
                  />
                  <input
                    type="tel"
                    placeholder="Telefon *"
                    value={volunteerForm.phone}
                    onChange={(e) => setVolunteerForm({...volunteerForm, phone: e.target.value})}
                    className="input-premium"
                    required
                    data-testid="volunteer-phone"
                  />
                  
                  <div>
                    <label className="block text-sm font-medium text-[#1A3625] mb-2">Becerileriniz</label>
                    <div className="flex flex-wrap gap-2">
                      {skillOptions.map((skill) => (
                        <button
                          key={skill}
                          type="button"
                          onClick={() => {
                            const newSkills = volunteerForm.skills.includes(skill)
                              ? volunteerForm.skills.filter(s => s !== skill)
                              : [...volunteerForm.skills, skill];
                            setVolunteerForm({...volunteerForm, skills: newSkills});
                          }}
                          className={`px-4 py-2 rounded-full text-sm transition-all ${
                            volunteerForm.skills.includes(skill)
                              ? 'bg-[#1A3625] text-white'
                              : 'bg-[#F9F8F6] text-[#5C5C5C] hover:bg-[#E5DAC8]'
                          }`}
                        >
                          {skill}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  <select
                    value={volunteerForm.availability}
                    onChange={(e) => setVolunteerForm({...volunteerForm, availability: e.target.value})}
                    className="input-premium"
                    required
                    data-testid="volunteer-availability"
                  >
                    <option value="">Müsaitlik Durumu *</option>
                    <option value="hafta-ici">Hafta içi</option>
                    <option value="hafta-sonu">Hafta sonu</option>
                    <option value="esnek">Esnek</option>
                  </select>
                  
                  <textarea
                    placeholder="Kendinizden ve motivasyonunuzdan bahsedin..."
                    value={volunteerForm.message}
                    onChange={(e) => setVolunteerForm({...volunteerForm, message: e.target.value})}
                    className="input-premium min-h-[100px]"
                    data-testid="volunteer-message"
                  />
                  
                  <button 
                    type="submit" 
                    className="btn-primary w-full"
                    disabled={loading}
                    data-testid="volunteer-submit"
                  >
                    {loading ? 'Gönderiliyor...' : 'Başvuru Yap'}
                  </button>
                </div>
              </form>
            </motion.div>
          )}

          {/* Sponsor */}
          {activeTab === 'sponsor' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="grid lg:grid-cols-2 gap-16"
            >
              <div>
                <h2 className="text-2xl text-[#1A3625] mb-6">Sponsor Olun</h2>
                <p className="text-[#5C5C5C] mb-8">
                  Kurumsal sosyal sorumluluk kapsamında projelerimizi destekleyin. 
                  Sponsorlarımız, sürdürülebilirlik ve toplum kalkınmasına katkı sağlar.
                </p>
                
                <h3 className="text-lg text-[#1A3625] mb-4">Sponsorluk Paketleri</h3>
                <div className="space-y-4">
                  {[
                    { name: 'Bronz', price: '10.000 TL', benefits: ['Logo görünürlüğü', 'Sosyal medya paylaşımı'] },
                    { name: 'Gümüş', price: '25.000 TL', benefits: ['Bronz avantajları', 'Etkinlik davetleri', 'Basın bülteni'] },
                    { name: 'Altın', price: '50.000 TL', benefits: ['Gümüş avantajları', 'Özel etkinlik', 'Yıllık raporda yer alma'] }
                  ].map((pkg, index) => (
                    <div key={index} className="bg-[#F9F8F6] rounded-2xl p-6">
                      <div className="flex justify-between items-start mb-4">
                        <h4 className="font-medium text-[#1A3625]">{pkg.name} Sponsor</h4>
                        <span className="text-[#D4AF37] font-semibold">{pkg.price}</span>
                      </div>
                      <ul className="space-y-2">
                        {pkg.benefits.map((benefit, i) => (
                          <li key={i} className="flex items-center gap-2 text-base text-[#5C5C5C]">
                            <Check className="w-4 h-4 text-[#D4AF37]" />
                            {benefit}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
              
              <form onSubmit={handleSponsorSubmit} className="card-premium" data-testid="sponsor-form">
                <h3 className="text-lg text-[#1A3625] mb-6">Sponsorluk Başvurusu</h3>
                
                <div className="space-y-4">
                  <input
                    type="text"
                    placeholder="Firma Adı *"
                    value={sponsorForm.company_name}
                    onChange={(e) => setSponsorForm({...sponsorForm, company_name: e.target.value})}
                    className="input-premium"
                    required
                    data-testid="sponsor-company"
                  />
                  <input
                    type="text"
                    placeholder="Yetkili Adı *"
                    value={sponsorForm.contact_name}
                    onChange={(e) => setSponsorForm({...sponsorForm, contact_name: e.target.value})}
                    className="input-premium"
                    required
                    data-testid="sponsor-contact"
                  />
                  <input
                    type="email"
                    placeholder="E-posta *"
                    value={sponsorForm.email}
                    onChange={(e) => setSponsorForm({...sponsorForm, email: e.target.value})}
                    className="input-premium"
                    required
                    data-testid="sponsor-email"
                  />
                  <input
                    type="tel"
                    placeholder="Telefon *"
                    value={sponsorForm.phone}
                    onChange={(e) => setSponsorForm({...sponsorForm, phone: e.target.value})}
                    className="input-premium"
                    required
                    data-testid="sponsor-phone"
                  />
                  
                  <select
                    value={sponsorForm.sponsorship_type}
                    onChange={(e) => setSponsorForm({...sponsorForm, sponsorship_type: e.target.value})}
                    className="input-premium"
                    required
                    data-testid="sponsor-type"
                  >
                    <option value="">Sponsorluk Türü *</option>
                    {sponsorshipTypes.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                  
                  <textarea
                    placeholder="Mesajınız..."
                    value={sponsorForm.message}
                    onChange={(e) => setSponsorForm({...sponsorForm, message: e.target.value})}
                    className="input-premium min-h-[100px]"
                    data-testid="sponsor-message"
                  />
                  
                  <button 
                    type="submit" 
                    className="btn-primary w-full"
                    disabled={loading}
                    data-testid="sponsor-submit"
                  >
                    {loading ? 'Gönderiliyor...' : 'Başvuru Yap'}
                  </button>
                </div>
              </form>
            </motion.div>
          )}
        </div>
      </section>
    </div>
  );
};
